import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { 
  Plus, Trash2, GripVertical, ChevronDown, ChevronUp, Eye, Copy, Link2,
  Type, AlignLeft, List, Upload, ToggleLeft, ArrowLeft, X,
  Sparkles, CalendarIcon, Star, ChevronsUpDown, Mail, Phone, CheckCircle2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../hooks/useLang";
import { useAuthContext } from "../hooks/useAuthContext";

const questionTypes = [
  { type: "short_text", label: "Short Text", icon: Type, description: "Single line answer", category: "Text" },
  { type: "long_text", label: "Long Text", icon: AlignLeft, description: "Multi-line paragraph", category: "Text" },
  { type: "email", label: "Email", icon: Mail, description: "Valid email address", category: "Text" },
  { type: "phone", label: "Phone", icon: Phone, description: "Valid phone number", category: "Text" },
  { type: "multiple_choice", label: "Multiple Choice", icon: List, description: "Select one option", category: "Choice" },
  { type: "checkbox", label: "Checkbox", icon: ToggleLeft, description: "Select multiple options", category: "Choice" },
  { type: "dropdown", label: "Dropdown", icon: ChevronsUpDown, description: "Select from list", category: "Choice" },
  { type: "rating", label: "Rating Scale", icon: Star, description: "Rate 1–5 stars", category: "Special" },
  { type: "date_picker", label: "Date Picker", icon: CalendarIcon, description: "Pick a date", category: "Special" },
  { type: "file_upload", label: "File Upload", icon: Upload, description: "Upload a file/PDF", category: "Special" },
];

const hasOptions = (type) => ["multiple_choice", "checkbox", "dropdown"].includes(type);

const CreateForm = () => {
  const navigate = useNavigate();
  const { urlLanguage } = useLang();
  const { user, token } = useAuthContext();
  
  const [formTitle, setFormTitle] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [questions, setQuestions] = useState([]);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [toastMsg, setToastMsg] = useState(null);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  const addQuestion = (type) => {
    const newQ = {
      id: `q_${Date.now()}`,
      text: "",
      type,
      required: false,
      options: hasOptions(type) ? [{ id: `o_${Date.now()}`, label: "" }, { id: `o_${Date.now() + 1}`, label: "" }] : undefined,
      ratingMax: type === "rating" ? 5 : undefined,
    };
    setQuestions([...questions, newQ]);
    setExpandedQuestion(newQ.id);
    setShowTypeSelector(false);
  };

  const updateQuestion = (id, updates) => setQuestions(questions.map(q => q.id === id ? { ...q, ...updates } : q));
  const removeQuestion = (id) => {
    setQuestions(questions.filter(q => q.id !== id));
    if (expandedQuestion === id) setExpandedQuestion(null);
  };

  const moveQuestion = (index, dir) => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= questions.length) return;
    const copy = [...questions];
    [copy[index], copy[newIndex]] = [copy[newIndex], copy[index]];
    setQuestions(copy);
  };

  const addOption = (qId) => setQuestions(questions.map(qt => qt.id === qId ? { ...qt, options: [...qt.options, { id: `o_${Date.now()}`, label: "" }] } : qt));
  const updateOption = (qId, optId, label) => setQuestions(questions.map(q => q.id === qId ? { ...q, options: q.options.map(o => o.id === optId ? { ...o, label } : o) } : q));
  const removeOption = (qId, optId) => setQuestions(questions.map(q => q.id === qId ? { ...q, options: q.options.filter(o => o.id !== optId) } : q));

  const handlePublish = async () => {
    if (!formTitle.trim()) return showToast("⚠️ Form title is required");
    if (questions.length === 0) return showToast("⚠️ Add at least one question");
    if (questions.some(q => !q.text.trim())) return showToast("⚠️ All questions must have text");
    
    try {
      const response = await fetch('/api/forms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: formTitle,
          description: formDescription,
          questions
        })
      });
      const data = await response.json();
      if (response.ok) {
        setGeneratedLink(`${window.location.origin}/v/${data.form.urlSlug}`);
        showToast("✨ Form Published Successfully!");
      } else {
        showToast(`⚠️ Error: ${data.message || 'Failed to publish'}`);
      }
    } catch (err) {
      console.error(err);
      showToast("⚠️ Network Error");
    }
  };

  const copyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      showToast("Link copied to clipboard!");
    }
  };

  const getTypeIcon = (type) => (questionTypes.find(t => t.type === type)?.icon || Type);
  const getTypeLabel = (type) => (questionTypes.find(t => t.type === type)?.label || type);
  const categories = Array.from(new Set(questionTypes.map(q => q.category)));

  const renderPreviewQuestion = (q, i) => (
    <div key={q.id} className="space-y-3 p-4 rounded-xl border border-border/40 bg-muted/20">
      <label className="text-base font-bold text-foreground flex items-center gap-1.5 leading-snug">
        <span className="text-muted-foreground font-mono text-sm">{i + 1}.</span> {q.text || "Untitled Question"}
        {q.required && <span className="text-pink-500 font-black text-lg leading-none">*</span>}
      </label>
      {q.description && <p className="text-[13px] text-muted-foreground leading-relaxed">{q.description}</p>}

      <div className="pt-2">
        {q.type === "short_text" && <input disabled placeholder="Short answer text" className="w-full sm:w-2/3 h-11 border-b-2 border-border/50 bg-transparent px-2 text-sm text-muted-foreground focus:outline-none focus:border-primary transition-colors hover:border-border cursor-not-allowed" />}
        {q.type === "long_text" && <textarea disabled placeholder="Long answer text" rows={3} className="w-full rounded-xl border-2 border-border/50 bg-transparent p-3 text-sm text-muted-foreground resize-none cursor-not-allowed" />}
        {q.type === "email" && <input disabled type="email" placeholder="example@domain.com" className="w-full sm:w-2/3 h-11 border-b-2 border-border/50 bg-transparent px-2 text-sm text-muted-foreground cursor-not-allowed" />}
        {q.type === "phone" && <input disabled type="tel" placeholder="+123 456 7890" className="w-full sm:w-2/3 h-11 border-b-2 border-border/50 bg-transparent px-2 text-sm text-muted-foreground cursor-not-allowed" />}
        
        {(q.type === "multiple_choice" || q.type === "checkbox") && (
          <div className="space-y-3 pl-2">
            {q.options?.map(o => (
              <label key={o.id} className="flex items-center gap-3 text-sm text-muted-foreground font-medium">
                <div className={`w-4 h-4 ${q.type === "checkbox" ? "rounded" : "rounded-full"} border-2 border-muted-foreground/50`} />
                {o.label || "Option"}
              </label>
            ))}
          </div>
        )}
        
        {q.type === "dropdown" && (
          <div className="w-full sm:w-2/3 h-11 rounded-xl border-2 border-border/50 bg-transparent px-4 flex items-center justify-between text-sm text-muted-foreground">
            <span>Select an option</span><ChevronsUpDown className="w-4 h-4 opacity-50" />
          </div>
        )}
        
        {q.type === "rating" && (
          <div className="flex gap-2 text-muted-foreground/30">
            {Array.from({ length: q.ratingMax || 5 }).map((_, si) => <Star key={si} className="w-8 h-8 hover:text-amber-400 transition-colors" strokeWidth={1.5} />)}
          </div>
        )}
        
        {q.type === "date_picker" && (
          <div className="w-full sm:w-64 h-11 rounded-xl border-2 border-border/50 px-4 flex items-center gap-3 text-sm text-muted-foreground">
            <CalendarIcon className="w-4 h-4 opacity-70" /> <span>mm/dd/yyyy</span>
          </div>
        )}
        
        {q.type === "file_upload" && (
          <div className="w-full sm:w-2/3 flex flex-col items-center justify-center p-6 rounded-xl border-2 border-dashed border-border/60 bg-muted/20">
            <Upload className="w-6 h-6 text-muted-foreground mb-2 opacity-50" />
            <span className="text-xs font-semibold text-muted-foreground">Click to upload file</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Toast Notification */}
      <AnimatePresence>
        {toastMsg && (
          <motion.div initial={{ opacity: 0, y: -20, x: "-50%" }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="fixed top-24 left-1/2 z-[200] bg-foreground text-background px-5 py-3 rounded-xl shadow-xl border border-foreground/10 text-sm font-bold flex items-center gap-2">
            {toastMsg.includes("✨") ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : null}
            {toastMsg}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-24 pb-32">
        {/* Top Actions */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={() => navigate(urlLanguage("/forms"))} className="flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-foreground transition-colors group">
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" /> Forms Dashboard
          </button>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowPreview(!showPreview)} className="h-10 px-5 rounded-full border border-border bg-card text-sm font-bold hover:bg-muted hover:border-muted-foreground/30 transition-all flex items-center gap-2 shadow-sm">
              <Eye className="w-4 h-4" /> {showPreview ? "Editor Mode" : "Preview Check"}
            </button>
            <button onClick={handlePublish} className="h-10 px-6 rounded-full bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-all flex items-center gap-2 shadow-lg shadow-primary/25 hover:shadow-primary/40 active:scale-95">
              <Sparkles className="w-4 h-4" /> Publish Now
            </button>
          </div>
        </div>

        {/* Generated Link Banner */}
        <AnimatePresence>
          {generatedLink && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mb-8 overflow-hidden">
              <div className="p-5 rounded-2xl bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-foreground">Form published successfully!</p>
                    <a href={generatedLink} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline truncate inline-block max-w-[200px] sm:max-w-[400px] font-medium">{generatedLink}</a>
                  </div>
                </div>
                <button onClick={copyLink} className="w-full sm:w-auto h-10 px-4 rounded-xl bg-card border border-border text-sm font-bold hover:bg-muted transition-all flex items-center justify-center gap-2 shadow-sm">
                  <Copy className="w-4 h-4" /> Copy Link
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {showPreview ? (
          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="bg-card rounded-[32px] border border-border p-8 sm:p-12 shadow-xl shadow-black/5 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-3 bg-gradient-to-r from-primary via-purple-500 to-blue-500" />
            
            <div className="mb-10 text-center">
              <h2 className="text-3xl font-display font-black text-foreground tracking-tight">{formTitle || "Untitled Form"}</h2>
              {formDescription && <p className="text-sm font-medium text-muted-foreground mt-3 max-w-xl mx-auto leading-relaxed">{formDescription}</p>}
            </div>
            
            <div className="space-y-8">
              {questions.length === 0 ? (
                <div className="text-center py-16 border-2 border-dashed border-border/50 rounded-2xl">
                  <p className="text-sm font-bold text-muted-foreground mb-1">It's a bit empty here</p>
                  <p className="text-xs text-muted-foreground/60">Switch to editor mode to add some questions.</p>
                </div>
              ) : (
                questions.map((q, i) => renderPreviewQuestion(q, i))
              )}
            </div>
            
            <div className="mt-12 flex justify-between items-center border-t border-border pt-6">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Powered by YeniDers</span>
              <button disabled className="h-11 px-8 rounded-full bg-primary text-primary-foreground text-sm font-bold opacity-50 cursor-not-allowed">
                Submit Answers
              </button>
            </div>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {/* Title & Description Editor */}
            <motion.div layout className="bg-card rounded-[24px] border border-border p-6 shadow-sm shadow-black/5 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-primary rounded-l-full" />
              <input
                value={formTitle} onChange={(e) => setFormTitle(e.target.value)}
                placeholder="Form Title Here"
                className="w-full text-3xl font-display font-black text-foreground bg-transparent placeholder:text-muted-foreground/40 border-none focus:outline-none focus:ring-0 mb-3"
              />
              <textarea
                value={formDescription} onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Add a brief description or instructions (optional)"
                rows={2}
                className="w-full text-sm font-medium text-muted-foreground bg-transparent placeholder:text-muted-foreground/40 border-none focus:outline-none focus:ring-0 resize-none p-0 m-0"
              />
            </motion.div>

            {/* Question List */}
            <AnimatePresence>
              {questions.map((q, index) => {
                const TypeIcon = getTypeIcon(q.type);
                const isExpanded = expandedQuestion === q.id;
                
                return (
                  <motion.div layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }} key={q.id}>
                    <div className={`bg-card rounded-2xl border transition-all duration-300 ${isExpanded ? "border-primary/50 shadow-md ring-4 ring-primary/5" : "border-border hover:border-primary/30"}`}>
                      
                      {/* Compact Header (Always visible unless fully editing maybe?) */}
                      <div className="flex items-center gap-3 p-4 select-none group" onClick={() => setExpandedQuestion(isExpanded ? null : q.id)}>
                        <div className="cursor-grab active:cursor-grabbing p-1.5 text-muted-foreground/30 hover:text-foreground hidden sm:block" onClick={e => e.stopPropagation()}>
                          <GripVertical className="w-5 h-5" />
                        </div>
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-black text-foreground shrink-0 border border-border/50">
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0 pr-4">
                          <p className={`text-sm font-bold truncate transition-colors ${!q.text ? "text-muted-foreground/50 italic" : "text-foreground"}`}>
                            {q.text || "Write your question here..."}
                          </p>
                          <div className="flex items-center gap-2 mt-1 -ml-0.5">
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground px-1.5 py-0.5 rounded-md bg-muted/60">
                              <TypeIcon className="w-3 h-3" /> {getTypeLabel(q.type)}
                            </span>
                            {q.required && <span className="text-[10px] font-black text-pink-500 bg-pink-500/10 px-1.5 py-0.5 rounded-md">REQUIRED</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                          <button onClick={() => moveQuestion(index, -1)} disabled={index === 0} className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground disabled:opacity-30 transition-colors">
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button onClick={() => moveQuestion(index, 1)} disabled={index === questions.length - 1} className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground disabled:opacity-30 transition-colors">
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          <div className="w-px h-4 bg-border mx-1" />
                          <button onClick={() => removeQuestion(q.id)} className="w-8 h-8 rounded-lg hover:bg-red-500/10 hover:text-red-500 flex items-center justify-center text-muted-foreground transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Expanded Editor */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden border-t border-border bg-muted/5">
                            <div className="p-5 sm:p-6 space-y-6">
                              <div className="space-y-3">
                                <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest px-1">Question Title</label>
                                <input
                                  autoFocus
                                  value={q.text} onChange={(e) => updateQuestion(q.id, { text: e.target.value })}
                                  placeholder="e.g. How satisfied are you with our service?"
                                  className="w-full h-12 rounded-xl bg-card border border-border px-4 text-base font-semibold text-foreground placeholder:font-normal placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all shadow-sm"
                                />
                                <input
                                  value={q.description || ""} onChange={(e) => updateQuestion(q.id, { description: e.target.value })}
                                  placeholder="Optional helper text or description"
                                  className="w-full h-10 rounded-xl bg-transparent border border-transparent px-4 text-sm font-medium text-muted-foreground placeholder:text-muted-foreground/40 focus:outline-none hover:bg-muted/50 focus:bg-card focus:border-border transition-all"
                                />
                              </div>

                              {hasOptions(q.type) && q.options && (
                                <div className="space-y-3 p-4 rounded-xl bg-card border border-border/60 shadow-sm">
                                  <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Answer Options</label>
                                  <div className="space-y-2">
                                    {q.options.map((opt, oi) => (
                                      <div key={opt.id} className="flex items-center gap-3">
                                        {q.type === "dropdown" ? (
                                          <span className="w-6 text-center text-xs font-bold text-muted-foreground">{oi + 1}.</span>
                                        ) : (
                                          <div className={`w-4 h-4 border-2 border-muted-foreground/40 shrink-0 ${q.type === "checkbox" ? "rounded-sm" : "rounded-full"}`} />
                                        )}
                                        <input
                                          value={opt.label} onChange={(e) => updateOption(q.id, opt.id, e.target.value)}
                                          placeholder={`Option ${oi + 1}`}
                                          className="flex-1 h-10 rounded-xl bg-transparent border border-border/60 px-3 text-sm font-medium text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary focus:bg-card transition-all"
                                        />
                                        {q.options.length > 2 && (
                                          <button onClick={() => removeOption(q.id, opt.id)} className="w-8 h-8 rounded-lg hover:bg-muted text-muted-foreground hover:text-red-500 transition-colors flex items-center justify-center shrink-0">
                                            <X className="w-4 h-4" />
                                          </button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                  <button onClick={() => addOption(q.id)} className="text-xs font-bold text-primary hover:text-primary/80 flex items-center gap-1.5 mt-2 transition-colors px-2 py-1 rounded-md hover:bg-primary/5">
                                    <Plus className="w-3.5 h-3.5" /> ADD NEW OPTION
                                  </button>
                                </div>
                              )}

                              {q.type === "rating" && (
                                <div className="space-y-3 p-4 rounded-xl bg-card border border-border/60 shadow-sm">
                                  <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Maximum Stars</label>
                                  <div className="flex gap-2">
                                    {[3, 4, 5, 7, 10].map(n => (
                                      <button key={n} onClick={() => updateQuestion(q.id, { ratingMax: n })} className={`h-10 w-12 rounded-xl text-sm font-bold transition-all shadow-sm ${ (q.ratingMax || 5) === n ? "bg-primary text-primary-foreground scale-105" : "bg-muted text-muted-foreground border border-border hover:border-muted-foreground/30 hover:text-foreground hover:bg-card" }`}>{n}</button>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {["date_picker", "email", "phone", "file_upload"].includes(q.type) && (
                                <div className="flex items-center gap-3 p-4 rounded-xl bg-primary/5 border border-primary/20 text-primary">
                                  <TypeIcon className="w-5 h-5" />
                                  <span className="text-sm font-semibold">User will see a specialized {getTypeLabel(q.type).toLowerCase()} input field for validation.</span>
                                </div>
                              )}

                              <div className="flex items-center justify-between border-t border-border pt-4 px-1">
                                <span className="text-xs font-bold text-foreground">Required Question</span>
                                <button onClick={() => updateQuestion(q.id, { required: !q.required })} className={`w-12 h-6 rounded-full transition-colors relative shadow-inner ${ q.required ? "bg-emerald-500" : "bg-muted-foreground/30" }`}>
                                  <motion.div layout className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow-sm`} initial={false} animate={{ x: q.required ? 24 : 0 }} transition={{ type: "spring", stiffness: 500, damping: 30 }} />
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {/* Add Question Button */}
            <div className="relative pt-4">
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }} onClick={() => setShowTypeSelector(!showTypeSelector)} className="w-full h-16 rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors flex items-center justify-center gap-2 text-sm font-bold text-primary shadow-sm hover:shadow-md hover:border-primary/50">
                <Plus className="w-5 h-5 transition-transform" /> ADD NEW QUESTION
              </motion.button>

              <AnimatePresence>
                {showTypeSelector && (
                  <motion.div initial={{ opacity: 0, y: 10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.98 }} className="absolute top-full mt-4 left-0 right-0 bg-card/80 backdrop-blur-xl rounded-[24px] border border-border shadow-2xl p-4 sm:p-6 z-20">
                    <div className="flex items-center justify-between mb-4 px-2">
                       <h3 className="text-sm font-bold text-foreground">Select Question Type</h3>
                       <button onClick={() => setShowTypeSelector(false)} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"><X className="w-4 h-4" /></button>
                    </div>
                    {categories.map((cat, i) => (
                      <div key={cat} className={i !== 0 ? "mt-6" : ""}>
                        <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest px-2 mb-2">{cat}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {questionTypes.filter(qt => qt.category === cat).map((qt) => (
                            <button key={qt.type} onClick={() => addQuestion(qt.type)} className="flex items-center gap-4 p-3 rounded-xl border border-transparent hover:border-primary/20 hover:bg-primary/5 transition-all text-left font-medium group">
                              <div className="w-10 h-10 rounded-xl bg-card border border-border shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                                <qt.icon className="w-5 h-5 text-foreground group-hover:text-primary transition-colors" />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-foreground">{qt.label}</p>
                                <p className="text-[10px] text-muted-foreground font-medium">{qt.description}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateForm;
